import React, { PureComponent } from "react";
import { withRouter } from "react-router-dom";
import MaterialTable from "components/MaterialTable/src/index";
import { connect } from "react-redux";
import { withErrorBoundary } from "BaseApp/ErrorBoundary/ErrorBoundary";
import { Typography, withStyles } from "@material-ui/core";
import FamilyApi from "MetaCell/api/Family";
import UserApi from "BaseApp/api/User";
import UserSelector from "BaseApp/selectors/User";
import "./FamilyMembers.css";
import Spinner from "components/Spinner/Spinner";
import { Button } from "@material-ui/core";
import { FamilySimpleFieldEditing } from "./FamilyEditComponents";
import FamilyHelper from "MetaCell/helper/Family";
import FamilyAction from "../../../../../../MetaCell/actions/Family";
import Helper from "MetaCell/helper/FamilyMember";
import DirectionSnackbar from "components/Snackbar/Snackbar";
import DeleteIcon from "@material-ui/icons/Delete";
import ConfirmDialogAction from "BaseApp/actions/ConfirmDialog";
import debounce from "lodash.debounce";
import BuildIcon from "@material-ui/icons/Build";
import DirectoryExplorerApi from "MetaCell/api/DirectoryExplorer";
import DirectoryExplorerAction from "MetaCell/actions/DirectoryExplorer";
import { FamilySelector } from "MetaCell/selectors/Family";
import { metaCellPaths } from "MetaCell/MetaCell";

const styles = {
  main: {
    width: "100%",
    boxSizing: "border-box"
  },
  loadingIndicator: {
    margin: "0 auto",
    display: "block"
  },
  table: {
    backgroundColor: "#f5f5f5"
  },
  exampleButton: {
    textTransform: "initial"
  }
};

const cellStyle = {
  backgroundColor: "#f5f5f5"
};
const headerStyle = {
  backgroundColor: "#f5f5f5"
};

/**
 * fields that must be filled in before submission
 */
export const requiredFields = ["alias"];

export class FamilyMembers extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      members: [],
      loading: true,
      invalidFields: [],
      progress: null,
      familySimulationLoading: false
    };
  }

  async componentDidMount() {
    await this.getCreateFamilyMemberJobStatus(
      this.props.familyId,
      this.fetchMembersAndSetReadyState
    );
  }

  /**
   * @callback
   * @param {Object} newData - the new family member data
   * @param {Object} oldData - the old family member data
   * @returns {Promise} a promise that calls the update member method
   */
  onRowUpdate = (newData, oldData) => {
    return new Promise((resolve, reject) => {
      const missingRequiredFields = FamilyHelper.getMissingRequiredProperties(
        newData,
        requiredFields
      );

      if (missingRequiredFields.length === 0 && oldData?.id) {
        FamilyApi.updateMember(oldData?.id, {
          alias: newData.alias,
          diff_summary: newData.diff_summary
        })
          .then(() => {
            const dataUpdate = [...this.state.members];
            const index = oldData.tableData.id;
            dataUpdate[index] = newData;
            this.setState({ members: [...dataUpdate] });
            resolve();
          })
          .catch(() => reject());
      } else {
        this.setState({ invalidFields: missingRequiredFields });
        reject();
      }
    });
  };

  /**
   * @callback
   * @param {Object} e - the click event
   * @param {Object[]} rowsData - the selected family members
   * @returns {Promise} a promise that calls the delete family member action
   */
  onRowDelete = (e, rowsData) => {
    const { deleteFamilyMemberAction, showConfirmDialog } = this.props;
    const familyMemberIds = rowsData.map(({ id }) => id);
    showConfirmDialog(
      "Delete",
      "Are you sure you want to delete the selected meta cells?",
      () => {
        deleteFamilyMemberAction(rowsData[0].family, familyMemberIds);
        const dataDelete = [
          ...this.state.members.filter(
            member => !familyMemberIds.includes(member.id)
          )
        ];
        this.setState({ members: [...dataDelete] });
      },
      undefined,
      false
    );
  };

  getCreateFamilyMembersJobStatusWithDelay = debounce(
    this.getCreateFamilyMemberJobStatus,
    5000
  );

  async getCreateFamilyMemberJobStatus(familyId, onFinish) {
    return FamilyApi.createFamilyMembersJobProgress(familyId, true)
      .then(resp => resp.data)
      .then(async data => {
        if (data.status === "QUEUED" || data.status === "RUNNING") {
          this.setState({
            progress: data.progress
          });
          return this.getCreateFamilyMembersJobStatusWithDelay(
            familyId,
            onFinish
          );
        } else if (data.status === "DONE" || data.status === "FAILED") {
          if (data.status === "DONE") {
            this.setState({
              progress: null
            });
            return await onFinish();
          } else {
            if (data.errors && data.errors["error"]) {
              this.setState({
                snackbar: {
                  visible: true,
                  message: data.errors["error"]
                }
              });
            }
          }
        }
      })
      .catch(async err => await onFinish());
  }

  fetchMembersAndSetReadyState = async () => {
    let members = await FamilyApi.fetchMembersByFamily(this.props.familyId);
    members = members.map((member, index) => {
      return {
        ...member,
        diff_summary: Helper.formatDiffSummary(member.diff_summary)
      };
    });
    members = await FamilyHelper.loadFamilyMemberLazyFields(members);
    this.setState({
      members,
      loading: false
    });
  };

  /**
   * it returns a style for the * sign in required fields
   * by checking their validation status
   * @param {String} field - the field to be checked
   */
  getRequiredStyle = field => {
    return {
      color: this.isFieldInvalid(field) ? "red" : "black"
    };
  };

  /**
   * it checks whether the given field exists in the state for invalid fields
   * @param {String} field - the family field
   * @returns {Boolean} whether it is invalid
   */
  isFieldInvalid = field => {
    const { invalidFields } = this.state;
    return invalidFields.indexOf(field) !== -1;
  };

  createFamilyProjectAndRedirect = async () => {
    const {
      addProjectItem,
      addSimulationItem,
      families,
      familyId,
      history,
      openSimulation,
      postUserSettings,
      showConfirmDialog
    } = this.props;
    const { user } = this.state;
    const familyName = families?.byId[familyId].name;
    if (familyId && families?.byId[familyId]) {
      this.setState({ familySimulationLoading: true });
      addProjectItem(`${familyName}_project`, user, familyId).then(project => {
        setTimeout(() => {
          addSimulationItem(`${familyName}_simulation`, project.id, familyId)
            .then(simulation => {
              openSimulation(simulation.id);
              postUserSettings({ lastSimulation: simulation.id });
              history.push(metaCellPaths.STRUCTURE);
              history.go();
            })
            .catch(() => {
              this.setState({ familySimulationLoading: false });
              showConfirmDialog(
                "Error",
                "An error occurred while creating the simulation.",
                null,
                null,
                false
              );
            });
        }, 400);
      });
    }
  };

  render = () => {
    const { classes, openMemberWizard } = this.props;
    const { familySimulationLoading, loading, members, progress } = this.state;
    const showDiscrepantThicknessWarning = !Helper.membersHaveSameThickness(
      members
    );
    return (
      <div
        className={`${classes.main} members-wrapper`}
        style={{
          paddingTop: "10px",
          paddingLeft: "10px",
          paddingRight: "10px",
          paddingBottom: "10px"
        }}
      >
        {familySimulationLoading && (
          <>
            <Typography align="center">
              Creating a simulation with the selected meta cell group.
            </Typography>
            <Spinner
              className={classes.loadingIndicator}
              name="Waiting"
              timeout={30000}
            />
          </>
        )}
        {!familySimulationLoading && loading && (
          <Spinner
            className={classes.loadingIndicator}
            name="Waiting"
            timeout={30000}
            progress={progress}
          />
        )}
        {!familySimulationLoading && !loading && (
          <MaterialTable
            title="Meta cells"
            columns={[
              {
                title: "Meta cell ID",
                field: "id",
                headerStyle,
                editable: "never"
              },
              {
                title: "Alias",
                field: "alias",
                headerStyle,
                editComponent: props => {
                  return (
                    <FamilySimpleFieldEditing
                      field={"alias"}
                      value={props.value || ""}
                      getRequiredStyle={this.getRequiredStyle}
                      isFieldInvalid={this.isFieldInvalid}
                      onChange={(field, value) => {
                        props.onChange(value);
                      }}
                    />
                  );
                }
              },
              {
                title: "Number of layers",
                field: "number_of_layers",
                headerStyle,
                editable: "never"
              },
              {
                title: "Total thickness",
                field: "total_thickness",
                headerStyle,
                editable: "never"
              },
              {
                title: "Diff summary",
                field: "diff_summary",
                headerStyle
              },
              {
                title: "",
                field: "example",
                editable: "never",
                render: rowData => (
                  <Button
                    className={classes.exampleButton}
                    variant="contained"
                    disabled
                  >
                    Example
                  </Button>
                )
              }
            ]}
            data={members}
            options={{
              selection: true,
              search: false,
              paging: false,
              sorting: false,
              draggable: false,
              maxBodyHeight: 350,
              headerStyle: { backgroundColor: "#f5f5f5" },
              cellStyle,
              actionsColumnIndex: -1
            }}
            editable={{
              isEditable: rowsData => rowsData.length === 1,
              onRowUpdate: this.onRowUpdate
            }}
            actions={[
              {
                icon: BuildIcon,
                tooltip: "Library analysis",
                isFreeAction: true,
                disabled:
                  this.state.members.length === 0 ||
                  this.state.familySimulationLoading,
                onClick: event => {
                  this.createFamilyProjectAndRedirect(this.props.familyId);
                }
              },
              {
                icon: "add",
                tooltip: "Add Meta cell",
                isFreeAction: true,
                onClick: event => {
                  openMemberWizard(this.props.familyId);
                }
              },
              {
                icon: DeleteIcon,
                tooltip: "Delete",
                onClick: this.onRowDelete
              }
            ]}
            localization={{
              pagination: {
                labelRowsSelect: "members"
              },
              header: {
                actions: ""
              }
            }}
          />
        )}
        {showDiscrepantThicknessWarning && (
          <DirectionSnackbar message="Meta cells of different thicknesses may lead to infeasible meta component designs" />
        )}
      </div>
    );
  };
}

const mapState = state => ({
  user: UserSelector.getUser(state),
  families: FamilySelector.getFamilies(state)
});

const mapDispatch = dispatch => ({
  addProjectItem: (item, selectedItem, familyId) =>
    dispatch(DirectoryExplorerApi.addProject(item, selectedItem, familyId)),
  addSimulationItem: (item, selectedItem, isFamilySimulation) =>
    dispatch(
      DirectoryExplorerApi.addSimulation(item, selectedItem, isFamilySimulation)
    ),
  openMemberWizard: familyId =>
    dispatch(
      FamilyAction.setMemberWizardOpen({
        openStatus: true,
        familyId: familyId
      })
    ),
  openSimulation: simulationId =>
    dispatch(DirectoryExplorerAction.openSimulation(simulationId)),
  deleteFamilyMemberAction: (familyId, familyMemberIds) =>
    dispatch(FamilyApi.deleteMembers(familyId, familyMemberIds)),
  postUserSettings: data => dispatch(UserApi.postUserSettings(data)),
  showConfirmDialog: (
    title,
    message,
    confirmAction,
    cancelAction,
    isReduxAction
  ) =>
    dispatch(
      ConfirmDialogAction.show(
        title,
        message,
        confirmAction,
        cancelAction,
        isReduxAction
      )
    )
});

export default connect(
  mapState,
  mapDispatch
)(withErrorBoundary(withStyles(styles)(withRouter(FamilyMembers))));
